import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { useI18n } from '../contexts/I18nContext';
import { useAuth } from '../contexts/AuthContext';
import { Translate, BellRinging, UserCircle, TelegramLogo, CaretDown } from '@phosphor-icons/react';
import TelegramConnect from '../components/TelegramConnect';
import { getNotificationLanguages, setMyNotificationLanguage } from '../lib/api';
import { NOTIFY_LANG_FALLBACK, notifyLangLabel } from '../lib/notifyLanguages';

function SettingRow({ icon: Icon, title, description, children, testid }) {
  return (
    <div
      className="flex items-start justify-between gap-6 py-6 border-b border-stone_line last:border-0"
      data-testid={testid}
    >
      <div className="flex items-start gap-4 min-w-0">
        <div className="w-10 h-10 rounded-xl bg-sage-100 text-sage-600 grid place-items-center shrink-0">
          <Icon size={20} weight="duotone" />
        </div>
        <div className="min-w-0">
          <div className="font-medium text-ink">{title}</div>
          <div className="text-sm text-ink-soft mt-1">{description}</div>
        </div>
      </div>
      <div className="shrink-0">{children}</div>
    </div>
  );
}

// ONE language control for everything: it sets the user's language on the
// backend (which drives Telegram + push notifications) AND switches the web UI
// immediately (mapped to the nearest available translation bundle).
function LanguageRow() {
  const { t, setLang } = useI18n();
  const { user, refreshMe } = useAuth();
  const [options, setOptions] = useState(NOTIFY_LANG_FALLBACK);
  const [value, setValue] = useState(user?.language || 'en');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getNotificationLanguages()
      .then((langs) => { if (Array.isArray(langs) && langs.length) setOptions(langs); })
      .catch(() => {});
  }, []);

  useEffect(() => { if (user?.language) setValue(user.language); }, [user?.language]);

  const onChange = async (e) => {
    const next = e.target.value;
    const prev = value;
    setValue(next);
    setLang(next);            // instant UI switch (canonical code, mapped internally)
    setSaving(true);
    try {
      await setMyNotificationLanguage(next);
      toast.success(t('language_saved'));
      await refreshMe();
    } catch (err) {
      const data = err.response?.data;
      // 422 = unsupported code — refresh options from the server's list.
      if (err.response?.status === 422 && Array.isArray(data?.supported)) {
        setOptions(data.supported);
      }
      setValue(prev);
      setLang(prev);
      toast.error(typeof data?.detail === 'string' ? data.detail : t('language_error'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <SettingRow
      icon={Translate}
      title={t('language')}
      description={t('language_desc')}
      testid="settings-language"
    >
      {/* Native <select> — keyboard/screen-reader friendly and matches mobile
          OS pickers, important for caregivers on tablets. */}
      <div className="relative">
        <select
          value={value}
          onChange={onChange}
          disabled={saving}
          data-testid="settings-lang-select"
          aria-label={t('language')}
          className="appearance-none bg-bg-alt border border-stone_line text-ink text-sm
                     rounded-lg pl-4 pr-9 py-2 cursor-pointer disabled:opacity-60
                     focus:outline-none focus:ring-2 focus:ring-sage-400 focus:border-sage-400
                     hover:border-sage-300 transition-colors min-w-[200px]"
        >
          {options.map((code) => (
            <option key={code} value={code} data-testid={`settings-lang-option-${code}`}>
              {notifyLangLabel(code)}
            </option>
          ))}
        </select>
        <CaretDown
          size={14}
          weight="bold"
          className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-ink-soft"
        />
      </div>
    </SettingRow>
  );
}

export default function Settings() {
  const { t } = useI18n();
  const { user } = useAuth();

  return (
    <div className="space-y-8">
      <header>
        <div className="text-xs uppercase tracking-[0.2em] text-sage-500 font-semibold mb-3">
          {t('nav_settings')}
        </div>
        <h1 className="font-heading text-5xl text-ink leading-none">{t('settings_title')}</h1>
        <p className="text-ink-soft mt-3 max-w-xl">{t('settings_sub')}</p>
      </header>

      <div className="bg-white border border-stone_line rounded-2xl shadow-soft p-8">
        <SettingRow
          icon={UserCircle}
          title={user?.name}
          description={`${user?.email} · ${user?.role}`}
          testid="settings-account"
        >
          <span className="text-xs uppercase tracking-[0.18em] text-ink-muted">{t('sign_in')}</span>
        </SettingRow>

        <LanguageRow />

        <SettingRow
          icon={BellRinging}
          title={t('notifications')}
          description="Email · SMS · Telegram"
          testid="settings-notifications"
        >
          <div className="flex gap-2 text-xs text-sage-600">
            <span className="bg-sage-100 px-3 py-1 rounded-full">Email</span>
            <span className="bg-sage-100 px-3 py-1 rounded-full">SMS</span>
            <span className="bg-sage-100 px-3 py-1 rounded-full">Telegram</span>
          </div>
        </SettingRow>

        <SettingRow
          icon={TelegramLogo}
          title={t('telegram_title')}
          description={t('telegram_desc')}
          testid="settings-telegram"
        >
          <TelegramConnect />
        </SettingRow>
      </div>
    </div>
  );
}
